import { isEmpty, toNumber } from 'lodash';
import { node } from 'prop-types';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { createSearchParams, generatePath, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { travelFormDefaultValues } from 'constants';
import { convertToCents, deepCaseConverter, formatDate, getValidDate } from 'helpers';
import { useFetch, useUser } from 'hooks';
import { routes } from 'router';
import { setTravelSteps, updateCurrentStepValidity } from 'store';

export const TravelPurchaseContext = createContext();

const TravelPurchaseProvider = ({ children }) => {
  /**
   * Form to manage the travel purchase
   * from the selection of the travel to the payment
   */
  const travelPurchaseForm = useForm({
    mode: 'onBlur',
    defaultValues: { ...travelFormDefaultValues }
  });
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const travelSteps = useSelector((state) => state.travelPurchase.steps);
  const { currentUser } = useUser();
  const [searchParams] = useSearchParams();

  const { call: userCall, data: user, loading: userLoading } = useFetch();
  const {
    call: departureCall,
    data: travelDeparture,
    loading: travelDepartureLoading,
    setData: setTravelDeparture
  } = useFetch({ withSetState: false });
  const { call: travelCall, data: travel, loading: travelLoading } = useFetch();
  const { call: travelPurchaseCall, loading: updateTravelPurchaseLoading } = useFetch({ withSetState: false });

  const currentStep = useMemo(() => travelSteps?.find((step) => step.current), [travelSteps]);
  const storedSteps = useSelector((state) => state.travelPurchase.steps);
  const storedDuration = useSelector((state) => state.travelPurchase.duration);
  const storedDepartureCity = useSelector((state) => state.travelPurchase.departureCity);
  const storedDepartureDate = useSelector((state) => state.travelPurchase.departureDate);
  const storedLodging = useSelector((state) => state.travelPurchase.lodging);
  const storedInsurance = useSelector((state) => state.travelPurchase.insurance);
  const storedParticipants = useSelector((state) => state.travelPurchase.participants);

  const travelId = searchParams.get('travelId');
  const typeItem = searchParams.get('typeItem');
  const orderId = searchParams.get('orderId');

  const isConfirmationStep = useMemo(() => {
    const confirmationStepRoute = routes.travelPurchase.children.confirmation.path;
    return location.pathname.includes(confirmationStepRoute);
  }, [location, orderId]);

  // -----------------------------------------------------------------------------------------------------
  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------
  useEffect(() => {
    getTravel();
    getUser();
  }, []);

  /**
   * Get informations about travel departure date, city, duration and passengers categories
   */
  useEffect(() => {
    if (currentStep?.order < 1 && isEmpty(travelDeparture)) {
      getTravelDeparture({
        from: storedDepartureCity?.value,
        duration: storedDuration?.value,
        monthYear: formatDate(storedDepartureDate, 'MM-yyyy')
      });
    }
  }, [currentStep]);

  useEffect(() => {
    const purchaseTravelRoute = routes.travelPurchase.path;
    /* Trigger effect only on travel purchase routes */
    if (location.pathname.includes(purchaseTravelRoute)) {
      const targetStep = travelSteps.find((step) => step.path === location.pathname);
      /*
        Update currentStep.current and targetStep.current if the conditions are fulfilled
        If the target step is after currentStep,
        then all previous steps of the target step should be valid and completed
        Navigation to previous steps is allowed without verification
      */
      if (targetStep?.order > currentStep.order) {
        const allPreviousStepsValid = storedSteps.slice(0, targetStep.order).every((step) => step.valid === true);
        if (allPreviousStepsValid) {
          dispatch(setTravelSteps({ stepToUpdate: currentStep, targetStep }));
        } else {
          navigateToStep(currentStep.slug);
        }
      } else if (targetStep?.order < currentStep?.order) {
        dispatch(setTravelSteps({ stepToUpdate: currentStep, targetStep }));
      }
    }
  }, [location]);

  // -----------------------------------------------------------------------------------------------------
  // Methods
  // -----------------------------------------------------------------------------------------------------

  /**
   *
   * @description Get the travel departure informations
   * (departure date, departure place, travel duration, passengers categories)
   */
  const getTravelDeparture = async (params = {}) => {
    if (!travelId || !typeItem) return;
    const formattedParams = deepCaseConverter(params, 'kebabCase');

    const { data } =
      (await departureCall(
        {
          service: 'castelis',
          path: `/product/travels/${currentUser.serialNumber}/departure/${travelId}/${typeItem}`,
          method: 'get',
          query: formattedParams
        },
        { hideToast: true }
      )) || {};
    const travelData = data?.body;

    if (travelData) {
      /**
       * Valid JS dates should be written in ISO format (yyyy-MM-dd)
       * API returns date in dd/MM/yyyy format so we need to convert it to a valid date
       */
      if (travelData?.dates?.length > 0) {
        travelData.dates = travelData.dates.map((date) => ({
          ...date,
          name: formatDate(getValidDate(date.name), 'yyyy-MM-dd'),
          returnDate: formatDate(getValidDate(date?.returnDate), 'yyyy-MM-dd')
        }));
      }
      setTravelDeparture(travelData);
    }
  };

  /**
   * Get the travel informations (name, price etc...)
   */
  const getTravel = () => {
    if (!travelId || !typeItem) return;

    travelCall({
      service: 'castelis',
      path: `/product/travels/${currentUser.serialNumber}/travel/${travelId}/catalogue/${typeItem}/`,
      method: 'get'
    });
  };

  const getUser = () => {
    userCall({
      service: 'castelis',
      path: `/users/${currentUser.serialNumber}`,
      method: 'get'
    });
  };

  /**
   * Create or Update travel cart
   */
  const updateTravelPurchase = async () => {
    const travelUnitPrice = convertToCents(storedLodging?.price) || convertToCents(travel?.priceNotC2C) || 0;

    const { data } =
      (await travelPurchaseCall({
        service: 'checkout',
        path: `/${currentUser.serialNumber}/cart/travel/products`,
        method: 'post',
        data: {
          lines: [
            {
              productId: travelId,
              quantity: 1,
              categoryId: travel?.travelCategory,
              typeItem: travel?.typeItem?.toString(),
              unitPrice: travelUnitPrice,
              options: getTravelOptions()
            }
          ]
        }
      })) || {};

    return data?.status;
  };

  const getTravelOptions = () => {
    let options = { optionsType: 'TRAVEL' };
    const {
      selection: { duration, from, selectedTravel }
    } = travelPurchaseForm.getValues();

    /**
     * voyage and participants are mandatory fields
     * Store value can be empty so we used form value as fallback
     */
    options.voyage = {
      departureDate: storedDepartureDate || selectedTravel.departureDate,
      departureCityId: toNumber(storedDepartureCity?.value) || toNumber(from.value),
      duration: toNumber(storedDuration?.value) || toNumber(duration.value)
    };
    options.participants = isEmpty(storedParticipants)
      ? [currentUser.serialNumber]
      : storedParticipants?.map((participant) => participant.serialNumber);

    if (storedLodging) {
      options.compositionId = storedLodging.compositionId;
      options.sojournId = storedLodging.sojournId;
    }
    if (storedInsurance?.id) {
      options.insurance = {
        unitPrice: storedInsurance.unitPrice,
        id: storedInsurance.id,
        scope: storedInsurance.scope
      };
    }

    return options;
  };

  /**
   * @description Method to navigate to the next step on click on the next button
   * Navigation from stepper click is handled on Step Components
   */
  const next = async () => {
    if (!isFormValid()) return;

    const status = await updateTravelPurchase();

    if (status === 200) {
      // Update the current step in the store
      dispatch(updateCurrentStepValidity({ valid: true, completed: true }));

      // Navigate to next step
      const nextStep = travelSteps.find((step) => step.order === currentStep.order + 1);
      navigateToStep(nextStep.slug);
    } else {
      toast.error(t('purchase:updateCartError'));
    }
  };

  /**
   * @description Method to navigate to the previous step on click on the previous button
   * Navigation from stepper click is handled on Step Components
   */
  const prev = () => {
    dispatch(updateCurrentStepValidity({ valid: isFormValid(), completed: isFormValid() }));
    if (currentStep.order === 0) {
      // Return to travel show page if we are on the first step and if there is a travelId in the search params
      if (searchParams.get('travelId')) {
        const productPath = generatePath(routes.travelShow.path, { id: searchParams.get('travelId') });

        navigate({
          pathname: productPath,
          search: createSearchParams({ typeItem }).toString()
        });
      } else {
        // Return to travel results page
        navigate({
          pathname: routes.results.path,
          search: createSearchParams({ type: 'travels' }).toString()
        });
      }
    } else {
      // Return to previous step
      const previousStep = travelSteps.find((step) => step.order === currentStep.order - 1);
      if (previousStep) navigateToStep(previousStep.slug);
    }
  };

  const navigateToStep = (stepSlug) => {
    const path = routes.travelPurchase.children[stepSlug].path;
    navigate({ pathname: path, search: createSearchParams({ typeItem, travelId }).toString() });
  };

  const isFormValid = () => {
    switch (currentStep.order) {
      case 0: {
        const selectedTravel = travelPurchaseForm.getValues('selection.selectedTravel');
        return !isEmpty(selectedTravel);
      }
      case 1: {
        const { participants, lodging } = travelPurchaseForm.getValues('configuration');
        return (
          participants?.every((participant) => participant.firstName && participant.name && participant.birthDate) &&
          lodging
        );
      }
      default:
        return travelPurchaseForm.formState.isValid;
    }
  };

  /**
   *
   * @returns Travel duration label (short or long)
   * @example
   * durationName({ short: true }) => '7J'
   * durationName({ short: false }) => '7 jours'
   */
  const durationName = ({ short }) => {
    if (short) {
      const shortDurations = travelDeparture?.durations?.map((duration) => ({
        ...duration,
        name: duration.name?.toLowerCase()?.replace(/jours/g, 'J').replace(/nuits/g, 'N').replace(/\s/g, '')
      }));
      return shortDurations?.[0]?.name || '';
    }

    return travelDeparture?.durations?.[0]?.name || '';
  };

  return (
    <TravelPurchaseContext.Provider
      value={{
        prev,
        next,
        user,
        updateTravelPurchaseLoading,
        userLoading,
        isConfirmationStep,
        // Helpers
        durationName,
        isFormValid,
        // Stepper,
        currentStep,
        navigateToStep,
        // Travel Product
        travel,
        travelLoading,
        // Travel Departure
        getTravelDeparture,
        travelDeparture,
        travelDepartureLoading
      }}
    >
      <FormProvider {...travelPurchaseForm}>{children}</FormProvider>
    </TravelPurchaseContext.Provider>
  );
};

export const useTravelPurchaseContext = () => useContext(TravelPurchaseContext);

TravelPurchaseProvider.propTypes = {
  children: node
};

export default TravelPurchaseProvider;
